import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Box, Card, Stack, Typography, Icons } from '@curves/ui-base';
import { ScrollableTable, ProductInfo } from '@curves/ui-shared';
import { InspectionProgress } from "@clubInventory/components";
import { CLUB_PURCHASE_RECEIPT_STATUS } from "@clubInventory/constants/dataEnums";

// 功能：商品清單欄位定義
const headerRow = [
	{
		cellKey: "productName",
		headerLabel: "商品清單",
		minWidth: 200,
		formatCell: (productName, row) => (
			<ProductInfo
				product={{
					name: productName,
					sku: row.sku,
					spec: row.variantName,
					imageUrl: row.pictureSrc,
				}}
			/>
		),
	},
	{
		cellKey: "completedQuantity",
		headerLabel: "已入庫",
		align: "center",
		minWidth: 100,
		formatCell: (_, row) => {
			const should = Number(row.shouldInspectionQuantity ?? 0);
			if (should === 0) return <Icons.Check color="success" />;
			return <Typography color="error">{row.completedQuantity ?? 0}</Typography>;
		},
	},
	{
		cellKey: "quantity",
		headerLabel: "訂購數量",
		align: "center",
		minWidth: 100,
	},
];

// 功能：進貨單商品清單（含驗收總進度）
const PurchaseOrderProductList = ({ products: list = [], totalQuantity = 0, totalReceived = 0, purchaseStatus, className }) => {
	const isExceptionClosed = purchaseStatus === CLUB_PURCHASE_RECEIPT_STATUS.EXCEPTIONCLOSED;
	const progressStatus = isExceptionClosed ? "error" : totalReceived === totalQuantity ? "success" : "primary";

	// shouldInspectionQuantity 為 0 的項目排到最後，且保持其餘項目的原順序
	const sortedList = useMemo(() => {
		if (!Array.isArray(list) || list.length === 0) return list ?? [];
		return list
			.map((row, idx) => ({ row, idx }))
			.sort((a, b) => {
				const aZero = Number(a.row.shouldInspectionQuantity ?? 0) === 0;
				const bZero = Number(b.row.shouldInspectionQuantity ?? 0) === 0;
				if (aZero === bZero) return a.idx - b.idx;
				return aZero ? 1 : -1;
			})
			.map(({ row }) => row);
	}, [list]);

	return (
		<Box>
			<Card className="p-2 bg-gray-50 mb-3">
				<Stack alignItems="center" justifyContent="space-between" spacing={2}>
					<Stack alignItems="center" spacing={2}>
						<Icons.ListUl />
						<Typography className="mb-0" color="secondary">
							驗收總進度
						</Typography>
					</Stack>

					<Box style={{ width: "230px" }}>
						<InspectionProgress numerator={totalReceived} denominator={totalQuantity} status={progressStatus} />
					</Box>
				</Stack>
			</Card>
			<ScrollableTable
				headerRow={headerRow}
				rowsData={sortedList}
				rowKey={(row) => `${row.productID}_${row.variantID ?? ""}`}
				className={className}
				tableClassName="theme-gray"
			/>
		</Box>
	);
};

PurchaseOrderProductList.propTypes = {
	products: PropTypes.array,
	totalQuantity: PropTypes.number,
	totalReceived: PropTypes.number,
	purchaseStatus: PropTypes.number,
	className: PropTypes.string,
	height: PropTypes.string,
};

export default PurchaseOrderProductList;
